import clsx from "clsx";
import { PRIMARY_BUTTON } from "@/constants";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import PaginationComponent from "@/app/components/ui/Pagination";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useEMRSectionAPI } from "@/app/hooks/api_hooks/Inventory/SubCategory/useEMRSectionAPI";
import { useSubCategoryListAPI } from "@/app/hooks/api_hooks/Inventory/SubCategory/useSubCategoryListAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { FiEdit } from "react-icons/fi";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { TbCircleLetterC } from "react-icons/tb";
import { useNavigate } from "react-router";

export interface ISubCategoryProps {
  type: string;
  emr: string;
}

const types = [
  {
    id: "1",
    title: "TAMS",
  },
  {
    id: "2",
    title: "Clinic",
  },
];

const PageSize = 10;

const tableHeader = [
  { item: "Sub Category Name" },
  { item: "Type" },
  { item: "EMR Section" },
  { item: "Action" },
];

const SubCategorySetupListing = () => {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const { NO_APPOINTMENTS } = useImageConstants();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const { emrSectionList, subCategoryList } = useRouteInfo("INVENTORY")?.routeState?.state as IInventoryState;
  const fetchEmrSectionList = useEMRSectionAPI();
  const isProductListInProgess = useLoader("isInventoryProductListLoadingInProgess");
  const { execute: fetchSubCategory } = useSubCategoryListAPI();
  const [_selectedType, setSelectedType] = useState<string | undefined>("");
  const typesList = useSelectMenuReducer(types, "title", "id", {
    withAllSelectOption: true,
    allSelectItemOptions: {
      title: "All",
    },
  });
  const emrOptions = useSelectMenuReducer(emrSectionList, "emr_category_name", "id", {
    withAllSelectOption: true,
    allSelectItemOptions: {
      title: "All",
    },
  });

  const formHook = useForm<ISubCategoryProps>({
    mode: "onChange",
    defaultValues: {
      type: "",
      emr: "",
    },
  });

  useEffect(() => {
    fetchEmrSectionList();
  }, []);

  const Submit = (data: ISubCategoryProps) => {
    const { type, emr } = data;
    const upperCaseType = type ? type.toUpperCase() : undefined;
    setSelectedType(upperCaseType);
    setCurrentPage(1);
    fetchSubCategory(1, PageSize, searchQuery, upperCaseType, emr);
  };

  useEffect(() => {
    fetchSubCategory(currentPage, PageSize, searchQuery);
  }, [currentPage, searchQuery]);

  return (
    <>
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-3 rounded-md shadow-sm">
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          <div className="flex-auto min-w-0">
            <h1 className="font-semibold text-sm text-gray-900 leading-6">VMS Sub-Categories</h1>
          </div>
          <div className="flex-none">
            <button
              type="button"
              onClick={() => navigate(getRouteKey("SUB_CATEGORY_ONBOARD", "url"))}
              className={`${PRIMARY_BUTTON} w-full sm:w-auto`}
            >
              Add New Sub-Category
            </button>
          </div>
        </div>
        <form className="flex justify-between items-center" onSubmit={formHook.handleSubmit(Submit)}>
          <div className="flex flex-col sm:flex-row sm:items-end w-full gap-3 sm:gap-5 mt-5">
            <div className="w-full sm:w-1/5 sm:mt-5">
              <SearchableSelectMenu
                errorMessages={[]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("type", item.title);
                  }
                }}
                fieldError={formHook.formState.errors.type}
                register={formHook.register("type", { required: false })}
                selectItems={typesList}
                placeholder="Select Type"
                showTooltips={true}
                showTypedErrors={false}
                showDropdownIcon={true}
                defaultSelected={typesList?.filter((oc) => oc.title === formHook.watch("type"))[0]}
                listBoxClassName="w-full"
                className="text-gray-800"
              />
            </div>
            <div className="w-full sm:w-1/5 sm:mt-5">
              <SearchableSelectMenu
                errorMessages={[]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("emr", item.title);
                  }
                }}
                fieldError={formHook.formState.errors.emr}
                register={formHook.register("emr", { required: false })}
                selectItems={emrOptions}
                placeholder="Select EMR Section"
                showTooltips={true}
                showTypedErrors={false}
                showDropdownIcon={true}
                defaultSelected={emrOptions?.filter((oc) => oc.title === formHook.watch("emr"))[0]}
                listBoxClassName="w-full"
                className="text-gray-800"
              />
            </div>
            <div>
              <input type="submit" className={`${PRIMARY_BUTTON} w-full sm:w-auto`} value={"Select"} />
            </div>
          </div>
        </form>
      </div>
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-3 rounded-md shadow-sm">
        <div>
          <InputWithAddOn
            onEnterKeyPress={(query: string) => setSearchQuery(query)}
            containerClassName="w-full sm:w-1/2 lg:w-1/3"
            addon={<TbCircleLetterC className="w-4 h-4" />}
            placeholder="Search by Sub Category Name"
          />
        </div>
        <div className="mt-6 sm:mt-8 flow-root">
          {isProductListInProgess ? (
            <TableSkeleton />
          ) : subCategoryList?.data?.length === 0 ? (
            <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No Data Found." />
          ) : (
            <div className="outer-container relative rounded-xl bg-[#F9FAFB]">
              <div className="scroll-inner !h-auto max-h-[600px]">
                <div className="w-full">
                  <div className="inline-block min-w-full align-middle">
                    <div className="bg-white rounded-lg">
                      <table className="w-full min-w-[640px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                        <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                          <tr>
                            {tableHeader?.map((key, index) => (
                              <th
                                key={index}
                                scope="col"
                                className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize"
                              >
                                {key?.item}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="overflow-hidden rounded-xl w-full">
                          {subCategoryList?.data?.map((sub, idx) => {
                            const isFirst = idx === 0;
                            const isLast = idx === subCategoryList?.data?.length - 1;
                            const isSingle = subCategoryList?.data?.length === 1;

                            return (
                              <tr
                                key={sub?.subcategory_id}
                                className={clsx(
                                  "group bg-white transition",
                                  isSingle && "rounded-xl",
                                  isFirst && !isSingle && "rounded-t-xl",
                                  isLast && !isSingle && "rounded-b-xl",
                                )}
                              >
                                <td
                                  className={clsx(
                                    "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                    isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                    isFirst && !isSingle && "rounded-tl-2xl",
                                    isLast && !isSingle && "rounded-bl-2xl",
                                  )}
                                >
                                  {sub?.subcategory_name}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">
                                  {sub?.subcategory_tagging}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">
                                  {sub?.emr_search}
                                </td>
                                <td
                                  className={clsx(
                                    "px-3 py-3 whitespace-nowrap text-xs font-normal cursor-pointer",
                                    isSingle && "rounded-tr-2xl rounded-br-2xl",
                                    isFirst && !isSingle && "rounded-tr-2xl",
                                    isLast && !isSingle && "rounded-br-2xl",
                                  )}
                                  onClick={() => {
                                    navigate(`/app/inventory/sub-category-onboard/${sub?.subcategory_id}`);
                                  }}
                                >
                                  <FiEdit className="w-4 h-4 text-gray-500 hover:text-black" />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {subCategoryList?.item_count && searchQuery.trim() === "" && (
            <div className="flex justify-center items-center p-4 w-full">
              <PaginationComponent
                className="pagination-bar"
                currentPage={currentPage}
                totalCount={subCategoryList?.item_count}
                pageSize={PageSize}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SubCategorySetupListing;
